import { useState } from 'react'
import { useAuthStore } from '../../auth/authStore'
import type { ProcurementAlert } from './types'

const DISMISSED_KEY = (userId: string): string => `crm_reminders_dismissed_${userId}`

function loadDismissed(userId: string | undefined): Set<string> {
  if (!userId) return new Set()
  try {
    const raw = localStorage.getItem(DISMISSED_KEY(userId))
    if (!raw) return new Set()
    const parsed = JSON.parse(raw) as unknown
    return Array.isArray(parsed) ? new Set(parsed.filter((x) => typeof x === 'string')) : new Set()
  } catch {
    return new Set()
  }
}

function saveDismissed(userId: string | undefined, set: Set<string>): void {
  if (!userId) return
  try {
    localStorage.setItem(DISMISSED_KEY(userId), JSON.stringify([...set]))
  } catch {
    /* ignore quota errors */
  }
}

const LEVEL: Record<
  ProcurementAlert['level'],
  { bg: string; title: string; desc: string; dot: string }
> = {
  critical: {
    bg: 'bg-red-50 border-red-200',
    title: 'text-red-900',
    desc: 'text-red-700',
    dot: '🔴',
  },
  warning: {
    bg: 'bg-amber-50 border-amber-200',
    title: 'text-amber-900',
    desc: 'text-amber-700',
    dot: '🟡',
  },
  info: {
    bg: 'bg-blue-50 border-blue-200',
    title: 'text-blue-900',
    desc: 'text-blue-700',
    dot: '🔵',
  },
}

export function RemindersPanel({
  alerts,
  onAlertClick,
}: {
  alerts: ProcurementAlert[]
  onAlertClick?: (alert: ProcurementAlert) => void
}) {
  const userId = useAuthStore((s) => s.user?.id)
  const [dismissed, setDismissed] = useState<Set<string>>(() => loadDismissed(userId))
  const visible = alerts.filter((a) => !dismissed.has(a.id))

  const dismiss = (id: string) => {
    const next = new Set(dismissed)
    next.add(id)
    setDismissed(next)
    saveDismissed(userId, next)
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4 h-full">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-gray-500">Напоминания</div>
        {visible.length > 0 && (
          <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-700">
            {visible.length}
          </span>
        )}
      </div>

      {visible.length === 0 ? (
        <p className="text-sm text-gray-400 py-2">Всё в порядке</p>
      ) : (
        <div className="space-y-2">
          {visible.map((a) => {
            const s = LEVEL[a.level]
            return (
              <div key={a.id} className={`relative rounded-lg border p-3 ${s.bg}`}>
                {/* Крестик «убрать» — не запускает onAlertClick. */}
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    dismiss(a.id)
                  }}
                  className="absolute top-1.5 right-1.5 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-700 text-base leading-none"
                  title="Убрать напоминание"
                  aria-label="Убрать напоминание"
                >
                  ×
                </button>
                <button
                  onClick={() => onAlertClick?.(a)}
                  className={`w-full text-left pr-5 ${
                    onAlertClick ? 'hover:opacity-95 cursor-pointer' : ''
                  }`}
                >
                  <div className={`text-sm font-medium ${s.title}`}>
                    {s.dot} {a.message}
                  </div>
                  {a.description && (
                    <div className={`text-xs mt-0.5 ${s.desc}`}>{a.description}</div>
                  )}
                </button>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
